import { Link, useNavigate } from 'react-router-dom'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMediaQueryLG } from '@/hooks/useMediaquery'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { requestPasswordReset } from '@/api/authApi'
import { toast } from 'sonner'
import axios from 'axios'
import { PATHROUTES } from '@/constants/path'

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .refine(val => /\.[a-z]{2,}$/i.test(val), {
      message: 'Email must end with a valid domain',
    }),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export default function ResetPassword() {
  const navigate = useNavigate()
  const isMobile = useMediaQueryLG()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onChange',
  })

  const mutation = useMutation({
    mutationFn: requestPasswordReset,
    onSuccess: (_data, variables) => {
      // navigate to message screen and carry email for resend
      navigate(PATHROUTES.AUTH.RESET_MESSAGE, {
        state: { email: variables.email },
      })
      toast.success(_data.message)
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Something went wrong. Please try again.'
        toast.error(message)
      }
    },
  })

  const onSubmit = (data: ForgotPasswordFormData) => {
    mutation.mutate({ email: data.email })
  }

  return (
    <main className="w-full" aria-label="Reset password request page">
      <header className="flex flex-col items-center lg:items-start gap-4 sm:gap-6 w-full">
        <img
          src="/auth/logo.svg"
          alt="SlopSync Logo"
          className="w-28 sm:w-48"
        />
        <div className="flex flex-col items-center lg:items-start gap-2 sm:gap-3 w-full">
          <div className="text-center lg:text-left">
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-gray-950 uppercase font-[ClashDisplay]">
              RESET YOUR
            </h1>
            <h1 className="text-xl lg:text-ss-lg leading-7 lg:leading-11 font-bold text-ss-blue-500 uppercase font-[ClashDisplay]">
              PASSWORD
            </h1>
          </div>
          <p className="text-sm lg:text-base font-semibold text-ss-gray-600 max-w-md text-center lg:text-left">
            Don't worry! Just enter your email, and we'll send instructions to
            help you set a new password.
          </p>
        </div>
      </header>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 w-full"
        aria-label="Reset password form"
        autoComplete="off"
      >
        <div className="flex flex-col gap-1.5 w-full">
          <label
            htmlFor="email"
            className="text-sm leading-6 font-medium text-secondary-foreground"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            inputSize={isMobile ? 'small' : 'large'}
            className={cn(
              errors.email &&
                'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20'
            )}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            aria-required="true"
            {...register('email')}
          />
          {errors.email && (
            <span
              className="text-sm text-ss-text-critical"
              id="email-error"
              role="alert"
            >
              {errors.email.message}
            </span>
          )}
        </div>
        <Button
          size={isMobile ? 'small' : 'large'}
          type="submit"
          disabled={!isValid || mutation.isPending}
          className="w-full"
          aria-label="Reset Password"
        >
          {mutation.isPending ? 'Sending...' : 'Reset Password'}
        </Button>
        <div className="flex items-center gap-1 text-center">
          <Link
            to="/auth/login"
            className="text-sm font-semibold text-ss-blue-500 hover:text-ss-blue-600 text-right sm:text-right flex justify-center w-full"
            aria-label="Back to Log In"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
            Back to Log In
          </Link>
        </div>
      </form>
    </main>
  )
}
